import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { API_BASE_URL } from '../config/api';
import { APP_RELEASE_VERSION } from '../config/appMeta';
import axios from 'axios';
import { store } from '../store/store';
import { showNotification } from '../store/slices/notificationPopupSlice';
import { setOrderIdToOpen } from '../store/slices/orderModalSlice';
import { playNotificationSound } from '../utils/notificationSound';

const PUSH_PERM_VERSION_KEY = 'restroai_push_perm_prompted_version';

class NotificationService {
  constructor() {
    this.fcmToken = null;
    this.isInitialized = false;
    this._initPromise = null;
    this.listeners = {
      registration: null,
      registrationError: null,
      pushNotificationReceived: null,
      pushNotificationActionPerformed: null,
    };
  }

  /**
   * Initialize push notifications
   */
  async initialize() {
    if (!Capacitor.isNativePlatform()) {
      console.log('Push notifications only work on native platforms');
      return;
    }

    if (this.isInitialized) {
      return;
    }

    if (this._initPromise) {
      return this._initPromise;
    }

    this._initPromise = this._runInitialize();
    try {
      await this._initPromise;
    } finally {
      this._initPromise = null;
    }
  }

  async _runInitialize() {
    const markPromptedForThisRelease = () => {
      try {
        localStorage.setItem(PUSH_PERM_VERSION_KEY, APP_RELEASE_VERSION);
      } catch (_) {}
    };

    try {
      if (!Capacitor.isPluginAvailable('PushNotifications')) {
        console.warn('PushNotifications plugin is not available on this platform');
        return;
      }

      // Create Notification Channel for Android (Critical for background/sound)
      if (Capacitor.getPlatform() === 'android') {
        try {
          await PushNotifications.createChannel({
            id: 'restaurant_notifications',
            name: 'Restaurant Notifications',
            description: 'Notifications for new orders and requests',
            importance: 5, // high
            visibility: 1, // public
            sound: 'default',
            vibration: true,
          });
          console.log('Notification channel created');
        } catch (e) {
          console.warn('Could not create notification channel', e);
        }
      }

      let lastPromptedVersion = null;
      try {
        lastPromptedVersion = localStorage.getItem(PUSH_PERM_VERSION_KEY);
      } catch (_) {}
      const firstOpenThisRelease = lastPromptedVersion !== APP_RELEASE_VERSION;

      let permStatus = await PushNotifications.checkPermissions();

      const shouldRequest =
        permStatus.receive === 'prompt' ||
        (firstOpenThisRelease && permStatus.receive !== 'granted');

      if (shouldRequest) {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (firstOpenThisRelease) {
        markPromptedForThisRelease();
      }

      if (permStatus.receive !== 'granted') {
        console.warn('Push notification permission not granted');
        return;
      }

      await this.setupListeners(PushNotifications);
      await PushNotifications.register();

      this.isInitialized = true;
      console.log('Push notifications initialized');
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  }

  /**
   * Set up push notification listeners (await handles so we never leave floating rejections).
   */
  async setupListeners(PN) {
    if (!PN) return;
    try {
      this.listeners.registration = await PN.addListener('registration', (token) => {
        console.log('Push registration success, token: ' + token.value);
        this.fcmToken = token.value;
        this.sendTokenToBackend(token.value);
      });

      this.listeners.registrationError = await PN.addListener('registrationError', (error) => {
        console.error('Error on registration: ' + JSON.stringify(error));
      });

      this.listeners.pushNotificationReceived = await PN.addListener(
        'pushNotificationReceived',
        async (notification) => {
          console.log('Push notification received: ', notification);
          const data = notification.data || {};
          const title = notification.title || 'New Order';
          const body = notification.body || data.message || 'You have a new notification';
          const orderId = data.orderId || null;
          playNotificationSound();
          store.dispatch(showNotification({ title, message: body, orderId }));
          // Ensure visible system popup when app is in foreground.
          await this.showLocalNotification(notification);
        }
      );

      this.listeners.pushNotificationActionPerformed = await PN.addListener(
        'pushNotificationActionPerformed',
        (notification) => {
          console.log('Push notification action performed', notification.actionId, notification.notification);
          this.handleNotificationAction(notification);
        }
      );
    } catch (e) {
      console.warn('Push notification listeners setup failed:', e);
    }
  }

  /**
   * Send FCM token to backend
   */
  async sendTokenToBackend(token) {
    try {
      const authToken = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      
      if (!authToken || !userData) {
        console.log('User not logged in, token will be sent after login');
        // Store token to send later
        this.pendingToken = token;
        return;
      }

      const user = JSON.parse(userData);
      const restaurantData = localStorage.getItem('restaurantData');
      const restaurantId = user.restaurantId
        || (restaurantData ? (JSON.parse(restaurantData)._id || null) : null)
        || localStorage.getItem('restaurantId');

      await axios.post(
        `${API_BASE_URL}/notifications/register-token`,
        {
          fcmToken: token,
          userId: user.id,
          userRole: user.role,
          restaurantId
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        }
      );
      
      console.log('FCM token sent to backend successfully');
      this.pendingToken = null;
    } catch (error) {
      console.error('Error sending FCM token to backend:', error);
    }
  }

  /**
   * Send pending token after login
   */
  async sendPendingToken() {
    if (this.pendingToken) {
      await this.sendTokenToBackend(this.pendingToken);
    } else if (this.fcmToken) {
      // If token is already registered, try sending it again
      await this.sendTokenToBackend(this.fcmToken);
    }
  }

  /**
   * Show local notification with sound (for foreground notifications)
   */
  async showLocalNotification(notification) {
    if (Capacitor.isNativePlatform()) {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      
      try {
        // Request permission for local notifications
        const permStatus = await LocalNotifications.checkPermissions();
        if (permStatus.display === 'prompt') {
          await LocalNotifications.requestPermissions();
        }

        // Show notification with sound
        await LocalNotifications.schedule({
          notifications: [
            {
              title: notification.title || 'New Notification',
              body: notification.body || notification.data?.message || 'You have a new notification',
              id: Date.now(),
              sound: 'default',
              priority: 'high',
              data: notification.data,
            }
          ]
        });
      } catch (error) {
        console.error('Error showing local notification:', error);
      }
    }
  }

  /**
   * Handle notification action (when user taps notification).
   * Store orderId so when app loads /restaurant we open the order details modal.
   */
  handleNotificationAction(notification) {
    const data = notification.notification?.data || {};
    if (data.type === 'order' && data.orderId) {
      const id = String(data.orderId);
      try {
        localStorage.setItem('pendingOrderIdFromPush', id);
      } catch (e) {}
      store.dispatch(setOrderIdToOpen(id));
      return;
    }
    if (data.type === 'waiter_request') {
      window.location.href = '/restaurant';
    }
  }

  /**
   * Get current FCM token
   */
  getToken() {
    return this.fcmToken;
  }

  /**
   * Remove all listeners
   */
  removeAllListeners() {
    const remove = (h) => {
      if (h?.remove) {
        Promise.resolve(h.remove()).catch(() => {});
      }
    };
    remove(this.listeners.registration);
    remove(this.listeners.registrationError);
    remove(this.listeners.pushNotificationReceived);
    remove(this.listeners.pushNotificationActionPerformed);
  }
}

// Export singleton instance
export default new NotificationService();
